'use client';

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { StripeGradient, StripeGradientConfig } from './stripe-gradient';

export interface UseGradientOptions extends Partial<StripeGradientConfig> {
  /**
   * 自動再生するかどうか
   * @default true
   */
  autoplay?: boolean;
}

export interface UseGradientReturn {
  /**
   * キャンバス要素にアタッチするref
   */
  canvasRef: React.RefObject<HTMLCanvasElement>;
  /**
   * アニメーションを開始
   */
  play: () => void;
  /**
   * アニメーションを一時停止
   */
  pause: () => void;
  /**
   * カラーを動的に更新
   */
  updateColors: (colors: string[]) => void;
}

/**
 * メッシュグラデーションを使用するためのReactフック
 *
 * @example
 * ```tsx
 * const { canvasRef } = useGradient({
 *   colors: ['#ef008f', '#6ec3f4', '#7038ff', '#ffba27'],
 * });
 *
 * return <canvas ref={canvasRef} className="w-full h-full" />;
 * ```
 */
export function useGradient(
  options: UseGradientOptions = {},
): UseGradientReturn {
  const { autoplay = true, ...gradientConfig } = options;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gradientRef = useRef<StripeGradient | null>(null);

  // colorsとdensityをメモ化（配列の比較のため）
  const colorsKey = useMemo(
    () => JSON.stringify(gradientConfig.colors),
    [gradientConfig.colors],
  );
  const densityKey = useMemo(
    () => JSON.stringify(gradientConfig.density),
    [gradientConfig.density],
  );

  // Gradientインスタンスを初期化
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Canvas要素のサイズが確定するまで待つ
    const initGradient = () => {
      // Canvas要素のサイズを確認
      const width = canvas.clientWidth || canvas.width || 640;
      const height = canvas.clientHeight || canvas.height || 480;

      // サイズが0の場合は、次のフレームで再試行
      if (width === 0 || height === 0) {
        requestAnimationFrame(initGradient);
        return;
      }

      try {
        // StripeGradientを作成して接続
        const gradient = new StripeGradient(gradientConfig);
        const success = gradient.connect(canvas);

        if (success) {
          gradientRef.current = gradient;

          // 自動再生
          if (autoplay) {
            gradient.play();
          }
        } else {
          console.error('Failed to connect gradient to canvas');
        }
      } catch (error) {
        console.error('Failed to initialize gradient:', error);
      }
    };

    // 次のフレームで初期化（DOMのレイアウトが確定してから）
    requestAnimationFrame(initGradient);

    // クリーンアップ
    return () => {
      if (gradientRef.current) {
        gradientRef.current.disconnect();
        gradientRef.current = null;
      }
    };
    // gradientConfigの変更（特にcolors）を検知して再初期化
    // colorsとdensityは配列なので、JSON.stringifyで文字列化して比較
  }, [
    autoplay,
    colorsKey,
    gradientConfig.speed,
    gradientConfig.amplitude,
    densityKey,
    gradientConfig.angle,
    gradientConfig.seed,
    gradientConfig.wireframe,
  ]);

  const play = useCallback(() => {
    gradientRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    gradientRef.current?.pause();
  }, []);

  const updateColors = useCallback((_colors: string[]) => {
    // SimpleGradientでは動的なカラー更新はサポートしていない
    // 将来的に実装する場合はここに追加
    console.warn('Dynamic color update is not supported yet');
  }, []);

  return {
    canvasRef,
    play,
    pause,
    updateColors,
  };
}
